import React, { useEffect, useId, useRef, useState } from 'react';
import { useLionAutoStatus } from '../../hooks/useLionAuto';
import {
  floorApplyTexts, floorRepriceCount, floorRepriceText, floorRepriceNotes, floorNextRunText, count,
} from '../../utils/lionAutoFloors';

// The Floors box's save dialog for a floor above 0 (LaFloorsPanel opens it
// when floorApplyChoices has something to choose). Two radios: "Only groups
// that follow" (today's save, the default) or "Everything" (the groups in
// scope with their own floor, and for the tour every upcoming show's show
// floor, are cleared so they follow it); no radios when nothing has a floor
// of its own. allBlocked (applyAllBlockedReason: a saved group is open in
// the editor, and its Save would put a cleared floor back) disables
// "Everything" with the reason as its hint. "Update prices now" (canReprice:
// admin-like only; the API enforces it) queues the Auto groups whose floor
// changes with the chosen radio (the count is an upper bound); off when
// none would. The runner's state (switched off, no working login, dry run,
// still reading) shows under it. Without that box, the Auto groups the
// choice moves are named as the old confirm did (they use it on their next
// run). It never calls the API: onConfirm({ applyToAll, repriceNow }) and
// the panel saves; while it saves (saving) the dialog stays open: no
// Escape, no overlay click, no Cancel.
//
// Props: choices (floorApplyChoices), performerName, allBlocked, saving,
// onConfirm, onCancel.

const SAVING_TITLE = 'Wait for the save to finish';

// Where the focus goes back on close: the opener, else (a saved row's Save
// is disabled by then: nothing left to save) the first live box of its form
function returnFocusTarget(opener) {
  if (!opener || !opener.isConnected) return null;
  if (opener.disabled && opener.form) return opener.form.querySelector('input:not(:disabled)');
  return opener;
}

function LaFloorApplyModal({ choices, performerName = null, allBlocked = null, saving = false, onConfirm, onCancel }) {
  const titleId = useId();
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const [pickedAll, setPickedAll] = useState(false);
  const [repriceTicked, setRepriceTicked] = useState(false);
  const canReprice = !!choices?.canReprice;
  // The runner's state: only an admin-like user reads it (the page shares the cache)
  const statusQ = useLionAutoStatus({ enabled: canReprice });

  const texts = floorApplyTexts(choices, performerName);
  const hasExceptions = count(choices?.exceptions) > 0;
  const applyToAll = hasExceptions && pickedAll && !allBlocked;
  const repriceCount = floorRepriceCount(choices, applyToAll);
  const repriceNow = canReprice && repriceTicked && repriceCount > 0;
  const notes = canReprice ? floorRepriceNotes(statusQ.data, { pending: statusQ.isPending, failed: statusQ.isError }) : [];
  const radioName = `${titleId}-scope`;

  const handleClose = () => {
    if (!saving) onCancel();
  };
  closeRef.current = handleClose;

  // Escape closes (not while the save runs)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      closeRef.current?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Focus the dialog on open; give it back to the opener (or its row's box) on close
  useEffect(() => {
    const opener = document.activeElement;
    dialogRef.current?.focus();
    return () => {
      const back = returnFocusTarget(opener);
      if (back && typeof back.focus === 'function') back.focus();
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!saving) onConfirm({ applyToAll, repriceNow });
  };

  const radios = hasExceptions ? (
    <div className="la-floor-apply-options" role="radiogroup" aria-label="Which groups take the new floor">
      <label className="la-floor-apply-option">
        <input type="radio" name={radioName} checked={!applyToAll} disabled={saving} onChange={() => setPickedAll(false)} />
        <span className="la-floor-apply-text">
          <span className="la-floor-apply-label">Only groups that follow</span>
          <span className="la-hint">{texts.followHint}</span>
        </span>
      </label>
      <label className="la-floor-apply-option" title={allBlocked || undefined}>
        <input
          type="radio"
          name={radioName}
          checked={applyToAll}
          disabled={saving || !!allBlocked}
          onChange={() => setPickedAll(true)}
        />
        <span className="la-floor-apply-text">
          <span className="la-floor-apply-label">{texts.allLabel}</span>
          <span className={`la-hint${allBlocked ? ' warn' : ''}`}>{allBlocked || texts.allHint}</span>
        </span>
      </label>
    </div>
  ) : (
    <div className="la-hint">{texts.noExceptions}</div>
  );

  const nextRun = canReprice ? null : floorNextRunText(repriceCount, { upTo: true });
  const repriceOff = !repriceCount;
  const repriceBox = canReprice && (
    <div className="la-floor-apply-reprice">
      <label className="la-floor-apply-option" title={repriceOff ? floorRepriceText(0) : undefined}>
        <input
          type="checkbox"
          checked={repriceNow}
          disabled={saving || repriceOff}
          onChange={(e) => setRepriceTicked(e.target.checked)}
        />
        <span className="la-floor-apply-text">
          <span className="la-floor-apply-label">Update prices now</span>
          <span className="la-hint">{floorRepriceText(repriceCount)}</span>
        </span>
      </label>
      {notes.map(note => (
        <div key={note.text} className={`la-hint la-floor-apply-note${note.warn ? ' warn' : ''}`}>{note.text}</div>
      ))}
    </div>
  );

  return (
    <div className="modal-overlay" onClick={saving ? undefined : handleClose}>
      <div
        ref={dialogRef}
        className="modal modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id={titleId}>{texts.title}</h2>
          <button
            type="button"
            className="modal-close"
            onClick={handleClose}
            disabled={saving}
            title={saving ? SAVING_TITLE : undefined}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body la-floor-apply-body">
            {radios}
            {repriceBox}
            {nextRun && <div className="la-hint warn">{nextRun}</div>}
          </div>
          <div className="modal-footer bulk-review-footer la-floor-apply-foot">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClose}
              disabled={saving}
              title={saving ? SAVING_TITLE : undefined}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary la-floor-save" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default LaFloorApplyModal;
